import { Injectable } from '@angular/core';
import { ApiHttpClient } from '../../http/clients/api';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { map } from 'rxjs/operators';
import { Select } from '@ngxs/store';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'app/models/dto/lender-user.model';

interface Link {
    label: string;
    handler: Function;
}

interface Borrower {
    id: number;
    partnerGroup: string | null;
}

@Injectable()
export class LinkService {

    @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;

    constructor(private http: ApiHttpClient) {}


    // Removed due to legal concerns.  Add back once concerns/fix can be scoped.
    getLinks(borrower: Borrower): Link[] {
        return [
            {
                label: 'Application / Account',
                handler: this.addLogin.bind(this, borrower.id),
                enabled: borrower.partnerGroup !== 'jackhenry',
            },
            {
                label: 'Requests Upload',
                handler: this.addRequestLogin.bind(this, borrower.id),
                enabled: true
            },
            {
                label: 'Upload Documents',
                handler: this.addDocumentLogin.bind(this, borrower.id),
                enabled: true,
            },
            // Commented out (vs. deleted) per SMART-1105.
            // {
            //     label: 'Referral Url',
            //     handler: this.addRepReferralUrl.bind(this),
            //     enabled: true,
            // },
            {
                label: 'Renewal Application',
                handler: this.addRenewalLogin.bind(this, borrower.id),
                enabled: borrower.partnerGroup !== 'jackhenry',
            },
            {
                label: 'Renewal Signature',
                handler: this.addRenewalSignature.bind(this, borrower.id),
                enabled: true,
            },
            {
                label: 'Closing Stipulation Upload',
                handler: this.addClosingStipulationLogin.bind(this, borrower.id),
                enabled: true,
            },
            {
                label: 'Bank Connection',
                handler: this.addBankConnectionUrl.bind(this),
                enabled: true,
            },
            {
                label: 'Financing Options Page',
                handler: this.addOptionsPageLogin.bind(this, borrower.id),
                enabled: true,
            },
            {
              label: 'Debt Schedule',
              handler: this.addDebtSchedule.bind(this, borrower.id),
              enabled: true,
            }
        ].filter(link => link.enabled);
    }

    addPPPAppLink(borrowerId: number): Observable<string> {
    // Was covid-relief/business-info for original PPP app, PPP2 is covid-relief/getting-started
      return this.http.get<string>(`${environment.API_URL}/borrower-loginurl/` + borrowerId + '?r=covid-relief/getting-started');
    }

    addLogin(borrowerId: number): Observable<string> {
        return this.http.get<string>(`${environment.API_URL}/borrower-loginurl/` + borrowerId + '?selfselect=marketplace');
    }

    addRequestLogin(borrowerId: number): Observable<string> {
        return this.http.get<string>(`${environment.API_URL}/borrower-loginurl/` + borrowerId + '?r=upload/requests');
    }

    addDocumentLogin(borrowerId: number): Observable<string> {
        return this.http.get<string>(`${environment.API_URL}/borrower-loginurl/` + borrowerId + '?r=portal/documents');
    }

    addClosingStipulationLogin(borrowerId: number): Observable<string> {
        return this.http.get<string>(`${environment.API_URL}/borrower-loginurl/` + borrowerId + '?r=portal/closing');
    }

    addRepReferralUrl(): Observable<string> {
        return this.lenderUser$.pipe(
            map(user => {
                const userDetails = user.user ?  `${user.user.first.toLowerCase()}-${user.user.last.toLowerCase()}` : '';
                const repReferralUrl = `http://www.lendio.com/bp/referral/${userDetails}`;
                return repReferralUrl;
            })
        );
    }

    addRenewalLogin(borrowerId: number): Observable<string> {
        return this.http.get<string>(`${environment.API_URL}/borrower-loginurl/` + borrowerId + '?r=renewal&selfSelect=marketplace');
    }

    addRenewalSignature(borrowerId: number): Observable<string> {
        return this.http.get<string>(`${environment.API_URL}/borrower-loginurl/` + borrowerId + '?r=renewal/submit');
    }

    addBankConnectionUrl(): Observable<string> {
        return of(`${environment.BANK_CONNECTION_URL}`);
    }

    addOptionsPageLogin(borrowerId: number): Observable<string> {
        return this.http.get<string>(`${environment.API_URL}/borrower-loginurl/` + borrowerId + '?r=portal/options');
    }

    addDebtSchedule(borrowerId: number): Observable<string> {
      return this.http.get<string>(`${environment.API_URL}/borrower-loginurl/` + borrowerId + '?r=/sba/debt-schedule-upload?');
    }
}
